import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Maximize2, Minimize2, Square, Columns2, Grid2x2, Save, LogIn, CheckCircle2, Sun, Moon, Sparkles, AlertTriangle, X, Crown, Users, Camera, ChevronDown, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import ChartPane from "../components/ChartPane";
import WSAIView from "../components/WSAIView";
import LogTradeDialog from "../components/LogTradeDialog";
import EventCountdown from "../components/EventCountdown";
import { getClientId } from "../utils/clientId";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const mmss = (ms) => { const s = Math.max(0, Math.floor(ms / 1000)); return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };

const DEFAULT_PANES = [
  { symbol: "BINANCE:BTCUSDT", interval: "60", style: "1" },
  { symbol: "BINANCE:ETHUSDT", interval: "60", style: "1" },
  { symbol: "OANDA:XAUUSD", interval: "60", style: "1" },
  { symbol: "FX:EURUSD", interval: "60", style: "1" },
];

const LAYOUTS = [
  { cols: 1, icon: Square, label: "Single" },
  { cols: 2, icon: Columns2, label: "Split" },
  { cols: 4, icon: Grid2x2, label: "Quad" },
];

const WSAIChartPage = () => {
  const clientId = getClientId();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [cols, setCols] = useState(1);
  const [panes, setPanes] = useState([DEFAULT_PANES[0]]);
  const [theme, setTheme] = useState("light");
  const [isFull, setIsFull] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [dialog, setDialog] = useState({ open: false, context: null });
  const saveTimer = useRef(null);

  // Chart Auto-Pause: warn when a high-impact event is <=5 min away.
  const [calEvents, setCalEvents] = useState([]);
  const [nowTs, setNowTs] = useState(Date.now());
  const [warnDismissed, setWarnDismissed] = useState(null);
  useEffect(() => {
    (async () => {
      try { const d = await (await fetch(`${API}/news/calendar`, { credentials: "include" })).json(); setCalEvents(d.events || []); } catch { /* */ }
    })();
  }, []);
  useEffect(() => { const t = setInterval(() => setNowTs(Date.now()), 1000); return () => clearInterval(t); }, []);
  const warnEvent = (() => {
    const nowS = nowTs / 1000;
    const fut = calEvents.filter((e) => e.sort_ts && e.sort_ts > nowS && e.impact === "High").sort((a, b) => a.sort_ts - b.sort_ts);
    const e = fut[0];
    if (!e) return null;
    const msLeft = e.sort_ts * 1000 - nowTs;
    if (msLeft > 5 * 60 * 1000) return null;
    return { e, msLeft, id: `${e.title}-${e.sort_ts}` };
  })();
  const showWarn = warnEvent && warnDismissed !== warnEvent.id;

  // Owner "Post to VIP/Free" from chart snapshot (captures the tab, AI reads Entry/TP/SL/zone).
  const [isAdmin, setIsAdmin] = useState(false);
  const [postMenu, setPostMenu] = useState(false);
  const [posting, setPosting] = useState(false);
  const [tgAuto, setTgAuto] = useState(() => localStorage.getItem("ws_tg_auto") !== "0");
  useEffect(() => { localStorage.setItem("ws_tg_auto", tgAuto ? "1" : "0"); }, [tgAuto]);
  const [confirmSetup, setConfirmSetup] = useState(null); // {image, target, symbol, timeframe, direction, entry, tp, sl, rr, current}
  useEffect(() => { fetch(`${API}/vip/me`, { credentials: "include" }).then((r) => r.json()).then((d) => setIsAdmin(!!d.is_admin)).catch(() => {}); }, [user]);

  const captureTab = async () => {
    const stream = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: "browser" }, audio: false, preferCurrentTab: true });
    const video = document.createElement("video");
    video.srcObject = stream; await video.play();
    await new Promise((r) => setTimeout(r, 350));
    const vw = video.videoWidth, vh = video.videoHeight;
    const full = document.createElement("canvas");
    full.width = vw; full.height = vh;
    full.getContext("2d").drawImage(video, 0, 0);
    stream.getTracks().forEach((t) => t.stop());
    // Crop to JUST the price chart — target the TradingView widget (drops our app header),
    // then inset to strip TV's top toolbar, left drawing tools and bottom date-range tabs.
    const el = document.querySelector("#ws-chart-capture .ws-tv-chart") || document.getElementById("ws-chart-capture");
    if (el) {
      const r = el.getBoundingClientRect();
      const sx = vw / window.innerWidth, sy = vh / window.innerHeight;
      const insL = 52, insT = 44, insB = 38, insR = 2; // CSS px insets for TV chrome
      const cx = Math.max(0, Math.round((r.left + insL) * sx));
      const cy = Math.max(0, Math.round((r.top + insT) * sy));
      const cw = Math.min(vw - cx, Math.round((r.width - insL - insR) * sx));
      const ch = Math.min(vh - cy, Math.round((r.height - insT - insB) * sy));
      if (cw > 40 && ch > 40) {
        const out = document.createElement("canvas");
        out.width = cw; out.height = ch;
        out.getContext("2d").drawImage(full, cx, cy, cw, ch, 0, 0, cw, ch);
        return out.toDataURL("image/png");
      }
    }
    return full.toDataURL("image/png");
  };

  const postChart = async (target) => {
    setPostMenu(false);
    setPosting(true);
    const tid = toast.loading("Capturing chart — pick “This Tab” to include the chart…");
    try {
      const image_base64 = await captureTab();
      const p0 = panes[0] || {};
      const tfMap = { "1": "1m", "3": "3m", "5": "5m", "15": "15m", "30": "30m", "60": "1h", "120": "2h", "240": "4h", "D": "1D", "W": "1W" };
      const symbol = (p0.symbol || "").split(":").pop();
      const timeframe = tfMap[p0.interval] || p0.interval || "";
      toast.loading("AI is reading your setup…", { id: tid });
      const res = await fetch(`${API}/vip/post-chart`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_base64, target, symbol, timeframe, telegram_auto: tgAuto }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Could not post.", { id: tid }); }
      else if (target === "telegram") {
        toast.success(d.telegram_ok ? `Posted to Telegram ✅${tgAuto ? " · auto-updates on" : ""}` : "Posted, but Telegram send failed.", { id: tid });
      }
      else { toast.success(`Posted to ${target === "vip" ? "WS VIP" : "Free Community"} ✅`, { id: tid }); }
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "Screen capture cancelled." : "Could not capture the chart.", { id: tid });
    }
    setPosting(false);
  };

  const confirmPost = async () => {
    const c = confirmSetup;
    if (!c) return;
    if (!c.entry || !c.tp || !c.sl) { toast.error("Please fill Entry, TP and SL."); return; }
    setPosting(true);
    const tid = toast.loading("Posting your signal…");
    try {
      const res = await fetch(`${API}/vip/post-chart`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          image_base64: c.image, target: c.target, symbol: c.symbol, timeframe: c.timeframe,
          direction: c.direction, entry: c.entry, tp: c.tp, sl: c.sl,
        }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Could not post.", { id: tid }); }
      else { toast.success(`Posted to ${c.target === "vip" ? "WS VIP" : "Free Community"} ✅`, { id: tid }); setConfirmSetup(null); }
    } catch { toast.error("Could not post.", { id: tid }); }
    setPosting(false);
  };

  // Load saved layout on mount and whenever auth (user) changes — so a signed-in
  // user's layout follows them across devices.
  useEffect(() => {
    setLoaded(false);
    (async () => {
      try {
        const res = await fetch(`${API}/chart-prefs?client_id=${clientId}`, { credentials: "include" });
        const data = await res.json();
        if (data.prefs?.panes?.length) {
          setCols(data.prefs.cols || 1);
          setPanes(data.prefs.panes);
        }
      } catch {}
      setLoaded(true);
    })();
  }, [clientId, user]);

  // Persist layout (debounced) whenever it changes
  useEffect(() => {
    if (!loaded) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      fetch(`${API}/chart-prefs?client_id=${clientId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ cols, panes }),
      }).catch(() => {});
    }, 700);
    return () => clearTimeout(saveTimer.current);
  }, [cols, panes, loaded, clientId]);

  // Esc + scroll lock in fullscreen
  useEffect(() => {
    if (!isFull) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setIsFull(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [isFull]);

  const applyLayout = (n) => {
    setCols(n);
    setPanes((prev) => {
      const next = [...prev];
      while (next.length < n) next.push(DEFAULT_PANES[next.length] || DEFAULT_PANES[0]);
      return next.slice(0, n);
    });
  };

  const updatePane = useCallback((idx, pane) => {
    setPanes((prev) => prev.map((p, i) => (i === idx ? pane : p)));
  }, []);

  const logTrade = useCallback(({ symbol, interval }) => {
    setDialog({ open: true, context: { symbol, interval } });
  }, []);

  const submitTrade = useCallback(async (payload) => {
    try {
      const res = await fetch(`${API}/journal?client_id=${clientId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setDialog({ open: false, context: null });
      toast.success(`Logged ${payload.symbol} ${payload.side} · ${payload.interval} to AI Journal`);
    } catch {
      toast.error("Could not log trade. Try again.");
    }
  }, [clientId]);

  const gridClass = cols === 1 ? "grid-cols-1" : "grid-cols-2";
  const rowsForFull = cols === 4 ? "grid-rows-2" : "grid-rows-1";

  return (
    <div className={isFull ? "ws-chart-fullscreen" : "w-full px-6 py-6"}>
      <div className={`flex items-center justify-between flex-wrap gap-3 ${isFull ? "px-5 pt-4 pb-3" : "mb-4"}`}>
        <div className="flex items-center gap-2.5">
          <div className="ws-icon-chip w-9 h-9"><LineChart className="w-5 h-5" /></div>
          <div>
            <h1 className={`ws-heading font-bold ws-title-gradient ${isFull ? "text-[20px]" : "text-[28px]"}`}>WS Chart</h1>
            {!isFull && <p className="text-[12px] text-[color:var(--ws-muted)]">Multi-chart grid · full tools · auto-saved layout</p>}
          </div>
        </div>

        <EventCountdown variant="strip" onOpen={() => navigate("/ws-news-pro")} />

        <div className="flex items-center gap-3 flex-wrap">
          {isAdmin && (
            <div className="relative">
              <button onClick={() => setPostMenu((o) => !o)} disabled={posting} data-testid="chart-post-btn"
                className="ws-btn-primary px-3 py-1.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5">
                {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />} Post <ChevronDown className="w-3 h-3" />
              </button>
              {postMenu && (
                <div className="absolute left-0 top-[110%] z-30 w-56 p-1.5 rounded-xl" style={{ background: "var(--ws-panel,#0b1120)", border: "1px solid var(--ws-border-strong,rgba(148,163,184,0.25))", boxShadow: "0 12px 40px rgba(0,0,0,0.5)" }} data-testid="chart-post-menu">
                  <button onClick={() => postChart("vip")} data-testid="post-to-vip" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-[color:var(--ws-text)] hover:bg-[rgba(255,255,255,0.06)] transition-colors">
                    <Crown className="w-4 h-4 text-[#e6c25a]" /> Post to VIP
                  </button>
                  <button onClick={() => postChart("free")} data-testid="post-to-free" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-[color:var(--ws-text)] hover:bg-[rgba(255,255,255,0.06)] transition-colors">
                    <Users className="w-4 h-4 text-[#38bdf8]" /> Post to Free Community
                  </button>
                  <button onClick={() => postChart("telegram")} data-testid="post-to-telegram" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-[color:var(--ws-text)] hover:bg-[rgba(255,255,255,0.06)] transition-colors">
                    <Send className="w-4 h-4 text-[#29a9ea]" /> Post to Telegram
                  </button>
                  <div className="flex items-center justify-between px-3 py-2 mt-0.5 rounded-lg" style={{ background: "rgba(41,169,234,0.08)" }}>
                    <span className="text-[12px] text-[color:var(--ws-text)]">Auto trade updates</span>
                    <button onClick={() => setTgAuto((v) => !v)} data-testid="telegram-auto-toggle" className="relative w-9 h-5 rounded-full transition-colors" style={{ background: tgAuto ? "#29a9ea" : "rgba(120,140,180,0.3)" }}>
                      <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: tgAuto ? 18 : 2 }} />
                    </button>
                  </div>
                  <div className="text-[10px] text-[color:var(--ws-muted)] px-3 py-1.5 leading-snug">AI auto-detects Entry/TP/SL &amp; posts instantly. Telegram updates fire automatically on 1:1 / 2R / TP / SL.</div>
                </div>
              )}
            </div>
          )}
          {/* Layout selector */}
          <div className="flex items-center gap-1 ws-seg">
            {LAYOUTS.map((l) => (
              <button
                key={l.cols}
                onClick={() => applyLayout(l.cols)}
                className={`px-2.5 py-1.5 rounded-md text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${cols === l.cols ? "bg-[rgba(59,130,246,0.25)] text-white" : "text-[color:var(--ws-muted)] hover:text-white"}`}
                title={l.label}
              >
                <l.icon className="w-3.5 h-3.5" /> {l.label}
              </button>
            ))}
          </div>
          <span className="text-[11px] text-[color:var(--ws-muted)] inline-flex items-center gap-1.5">
            <Save className="w-3.5 h-3.5 text-[color:var(--ws-green)]" /> Layout saved
          </span>
          {user ? (
            <span className="ws-btn-ghost px-3 py-1.5 rounded-lg text-[12px] font-medium inline-flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--ws-green)]" />
              Synced · {user.name?.split(" ")[0] || "You"}
            </span>
          ) : (
            <button
              onClick={login}
              className="ws-btn-primary px-3 py-1.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-2"
              title="Sign in so your charts & positions sync across devices"
            >
              <LogIn className="w-3.5 h-3.5" /> Sign in with Google
            </button>
          )}
          <button
            onClick={() => setAiOpen((o) => !o)}
            className={`px-3 py-2 rounded-lg text-[13px] font-semibold inline-flex items-center gap-2 transition-colors ${aiOpen ? "ws-btn-primary" : "ws-btn-ghost"}`}
            title="Wealth Secret AI View — AI analysis of the selected chart"
            data-testid="ws-ai-view-btn"
          >
            <Sparkles className="w-4 h-4" /> Wealth Secret AI View
          </button>
          <button
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            className="ws-btn-ghost px-3 py-2 rounded-lg text-[13px] font-medium inline-flex items-center gap-2"
            title={theme === "light" ? "Switch to dark chart" : "Switch to light chart"}
            data-testid="chart-theme-toggle"
          >
            {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            {theme === "light" ? "Dark" : "Light"}
          </button>
          <button
            onClick={() => setIsFull((f) => !f)}
            className="ws-btn-ghost px-3 py-2 rounded-lg text-[13px] font-medium inline-flex items-center gap-2"
            title={isFull ? "Exit fullscreen (Esc)" : "Fullscreen"}
          >
            {isFull ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            {isFull ? "Exit" : "Fullscreen"}
          </button>
        </div>
      </div>

      {(() => {
        const warnBanner = showWarn ? (
          <div className="ws-chart-warn" data-testid="chart-event-warning" role="alert">
            <div className="ws-chart-warn-pulse"><AlertTriangle className="w-5 h-5" /></div>
            <div className="ws-chart-warn-body">
              <div className="ws-chart-warn-title">High-impact event in {mmss(warnEvent.msLeft)} — avoid opening new trades</div>
              <div className="ws-chart-warn-sub">{warnEvent.e.currency} · {warnEvent.e.title}{warnEvent.e.forecast ? ` · Forecast ${warnEvent.e.forecast}` : ""}</div>
            </div>
            <button onClick={() => setWarnDismissed(warnEvent.id)} className="ws-chart-warn-x" title="Dismiss" data-testid="chart-warning-dismiss"><X className="w-4 h-4" /></button>
          </div>
        ) : null;
        const chartGrid = (
          <div
            id="ws-chart-capture"
            className={`grid ${gridClass} ${isFull ? `${rowsForFull} flex-1 min-h-0 px-3 pb-3` : "flex-1 min-w-0"} gap-3`}
            style={isFull ? undefined : { height: "calc(100vh - 200px)", minHeight: 560 }}
          >
            {panes.map((pane, idx) => (
              <div key={idx} className="ws-panel p-0 min-h-0">
                <ChartPane pane={pane} onChange={(p) => updatePane(idx, p)} onLog={logTrade} theme={theme} rich={cols === 1} />
              </div>
            ))}
          </div>
        );
        if (isFull) return (<>{warnBanner}{chartGrid}</>);
        return (
          <div className="flex flex-col lg:flex-row gap-3">
            {warnBanner}
            {chartGrid}
            {aiOpen && <WSAIView pane={panes[0]} onClose={() => setAiOpen(false)} />}
          </div>
        );
      })()}

      <LogTradeDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        context={dialog.context}
        onSubmit={submitTrade}
      />

      {confirmSetup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(3,6,14,0.72)", backdropFilter: "blur(4px)" }} data-testid="confirm-post-modal">
          <div className="w-full max-w-[420px] rounded-2xl p-5" style={{ background: "var(--ws-panel,#0b1120)", border: "1px solid var(--ws-border-strong,rgba(148,163,184,0.25))", boxShadow: "0 24px 70px rgba(0,0,0,0.6)" }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[15px] font-bold text-[color:var(--ws-text)]">Review before posting</h3>
              <button onClick={() => setConfirmSetup(null)} data-testid="confirm-close" className="ws-btn-ghost w-7 h-7 rounded-lg flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[12px] text-[color:var(--ws-muted)] mb-4">AI read your {confirmSetup.symbol} {confirmSetup.timeframe} setup. Fix anything that's off, then post.{confirmSetup.current ? ` Live: ${confirmSetup.current}` : ""}</p>
            <div className="mb-3">
              <label className="text-[11px] uppercase tracking-wide text-[color:var(--ws-muted)]">Direction</label>
              <div className="flex gap-2 mt-1.5">
                {["Long", "Short"].map((dir) => (
                  <button key={dir} data-testid={`confirm-dir-${dir.toLowerCase()}`} onClick={() => setConfirmSetup((c) => ({ ...c, direction: dir }))}
                    className={`flex-1 py-2 rounded-lg text-[13px] font-bold transition-colors ${confirmSetup.direction === dir ? (dir === "Long" ? "bg-[rgba(34,197,94,0.22)] text-[#4ade80]" : "bg-[rgba(239,68,68,0.22)] text-[#f87171]") : "ws-btn-ghost"}`}>
                    {dir === "Long" ? "📈 LONG" : "📉 SHORT"}
                  </button>
                ))}
              </div>
            </div>
            {[{ k: "entry", l: "Entry", c: "#e6c25a" }, { k: "tp", l: "Take Profit", c: "#4ade80" }, { k: "sl", l: "Stop Loss", c: "#f87171" }].map((f) => (
              <div key={f.k} className="mb-3">
                <label className="text-[11px] uppercase tracking-wide" style={{ color: f.c }}>{f.l}</label>
                <input type="text" inputMode="decimal" value={confirmSetup[f.k]} data-testid={`confirm-${f.k}`}
                  onChange={(e) => setConfirmSetup((c) => ({ ...c, [f.k]: e.target.value }))}
                  className="w-full mt-1.5 px-3 py-2 rounded-lg text-[14px] text-[color:var(--ws-text)] outline-none"
                  style={{ background: "rgba(10,17,33,0.85)", border: "1px solid var(--ws-border,rgba(148,163,184,0.2))" }} />
              </div>
            ))}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setConfirmSetup(null)} data-testid="confirm-cancel" className="flex-1 ws-btn-ghost py-2.5 rounded-lg text-[13px] font-semibold">Cancel</button>
              <button onClick={confirmPost} disabled={posting} data-testid="confirm-post-btn" className="flex-1 ws-btn-primary py-2.5 rounded-lg text-[13px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Confirm &amp; Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WSAIChartPage;
